import { expiryDaysInMonth } from "@/lib/leave-expiry";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, Sun, Stethoscope, Star, BabyIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FloatWrapper } from "@/components/ui/FormField";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  apiGetLeaveTypes,
  apiCreateLeaveType,
  apiUpdateLeaveType,
  apiDeleteLeaveType,
  apiGetLeaveCompanyPolicy,
  apiUpdateLeaveCompanyPolicy,
  apiGetLeaveDocumentSettings,
  apiUpdateLeaveDocumentSettings,
  type LeaveTypeConfig,
  type LeaveCompanyPolicy,
  type LeaveDocumentSettings,
} from "@/lib/api";
import { ExtendedLeavePolicyTab } from "./ExtendedLeavePolicyTab";

// ── Leave type form schema ─────────────────────────────────────────────────────

const typeSchema = z.object({
  label: z.string().min(2).max(100),
  color: z.string().min(4),
  isPaid: z.boolean(),
  countsAgainstBalance: z.boolean(),
  requiresDocument: z.enum(["never", "always", "over_3_working_days"]),
  maxDaysPerYear: z.coerce.number().min(0).optional().nullable(),
  sortOrder: z.coerce.number().min(0).default(0),
});
type TypeForm = z.infer<typeof typeSchema>;

const CREATE_TYPE_DEFAULTS: TypeForm = {
  label: "",
  color: "#8b5cf6",
  isPaid: true,
  countsAgainstBalance: false,
  requiresDocument: "never",
  maxDaysPerYear: null,
  sortOrder: 20,
};

function typeFormValues(editing: LeaveTypeConfig | null): TypeForm {
  if (!editing) return { ...CREATE_TYPE_DEFAULTS };

  return {
    label: editing.label,
    color: editing.color,
    isPaid: editing.isPaid,
    countsAgainstBalance: editing.countsAgainstBalance,
    requiresDocument: editing.requiresDocument,
    maxDaysPerYear: editing.maxDaysPerYear ?? null,
    sortOrder: editing.sortOrder,
  };
}

// ── Specialty leave type dialog ────────────────────────────────────────────────

export function TypeDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: LeaveTypeConfig | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const form = useForm<TypeForm>({
    resolver: zodResolver(typeSchema),
    defaultValues: typeFormValues(editing),
  });

  useEffect(() => {
    if (open) form.reset(typeFormValues(editing));
  }, [editing, form, open]);

  const createMutation = useMutation({
    mutationFn: (data: TypeForm) =>
      apiCreateLeaveType({
        label: data.label,
        color: data.color,
        isPaid: data.isPaid,
        countsAgainstBalance: data.countsAgainstBalance,
        requiresDocument: data.requiresDocument,
        maxDaysPerYear: data.maxDaysPerYear ?? null,
        sortOrder: data.sortOrder,
      }),
    onSuccess: () => {
      toast.success(t("leave.specialtyTypeCreated"));
      qc.invalidateQueries({ queryKey: ["leaveTypes"] });
      onOpenChange(false);
    },
    onError: (err: Error & { code?: string }) => toast.error(
      err.code === "LEAVE_TYPE_LABEL_INVALID" || err.code === "LEAVE_TYPE_KEY_CONFLICT"
        ? t(`leave.errors.${err.code}`)
        : err.message,
    ),
  });

  const updateMutation = useMutation({
    mutationFn: (data: TypeForm) =>
      apiUpdateLeaveType(editing!.id, {
        label: data.label,
        color: data.color,
        isPaid: data.isPaid,
        countsAgainstBalance: data.countsAgainstBalance,
        requiresDocument: data.requiresDocument,
        maxDaysPerYear: data.maxDaysPerYear ?? null,
        sortOrder: data.sortOrder,
      }),
    onSuccess: () => {
      toast.success(t("leave.specialtyTypeUpdated"));
      qc.invalidateQueries({ queryKey: ["leaveTypes"] });
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {editing ? t("leave.editSpecialtyLeave") : t("leave.addSpecialtyLeave")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((d) =>
              editing ? updateMutation.mutate(d) : createMutation.mutate(d),
            )}
            className="space-y-1"
          >
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FloatWrapper label={t("leave.displayName")} required>
                    <FormControl>
                      <Input
                        placeholder={t("leave.displayNamePlaceholder")}
                        className="rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
                        {...field}
                      />
                    </FormControl>
                  </FloatWrapper>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FloatWrapper label={t("leave.colour")}>
                      <FormControl>
                        <div className="flex items-center gap-2 rounded-xl border border-[#d0d5e8] h-14 px-2">
                          <input
                            type="color"
                            className="h-8 w-10 rounded border-0 cursor-pointer"
                            {...field}
                          />
                          <input
                            className="flex-1 bg-transparent outline-none text-sm"
                            placeholder="#8b5cf6"
                            {...field}
                          />
                        </div>
                      </FormControl>
                    </FloatWrapper>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxDaysPerYear"
                render={({ field }) => (
                  <FormItem>
                    <FloatWrapper label={t("leave.daysAllowed")}>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder={t("leave.noLimit")}
                          className="rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(e.target.value === "" ? null : Number(e.target.value))
                          }
                        />
                      </FormControl>
                    </FloatWrapper>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="requiresDocument"
              render={({ field }) => (
                <FormItem>
                  <FloatWrapper label={t("leave.documentRequirement")}>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="never">{t("leave.neverRequired")}</SelectItem>
                        <SelectItem value="over_3_working_days">{t("leave.requiredOver3WorkingDays")}</SelectItem>
                        <SelectItem value="always">{t("leave.alwaysRequired")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FloatWrapper>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center gap-6">
              <FormField
                control={form.control}
                name="isPaid"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="font-normal">{t("leave.paidLeaveLabel")}</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="countsAgainstBalance"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="font-normal">{t("leave.usesBalance")}</FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("leave.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("leave.saving") : editing ? t("leave.saveChanges") : t("leave.addType")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function LeaveTypeManager() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const MONTHS = Array.from({ length: 12 }, (_, i) =>
    t(`leave.months.${i + 1}`)
  );
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<LeaveTypeConfig | null>(null);

  // ── Leave types query ──────────────────────────────────────────────────
  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: apiGetLeaveTypes,
  });

  // Specialty types = everything except annual and sick
  const specialtyTypes = leaveTypes.filter(
    (lt) => lt.name !== "annual" && lt.name !== "sick",
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteLeaveType(id),
    onSuccess: () => {
      toast.success(t("leave.leaveTypeRemoved"));
      qc.invalidateQueries({ queryKey: ["leaveTypes"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Company policy query ───────────────────────────────────────────────
  const { data: policy, isLoading: policyLoading } = useQuery({
    queryKey: ["leaveCompanyPolicy"],
    queryFn: apiGetLeaveCompanyPolicy,
  });

  // Local editable state for policy form (separate from query cache)
  const [policyForm, setPolicyForm] = useState<LeaveCompanyPolicy | null>(null);

  // Sync server data into local form once loaded (only once)
  const rawPolicy: LeaveCompanyPolicy = policyForm ?? policy ?? {
    annualLeaveDays: 20,
    annualLeaveCarryOverMax: 0,
    annualLeaveExpiryMonth: null,
    annualLeaveExpiryDay: null,
    sickLeaveDays: 15,
  };

  const effectivePolicy = { ...rawPolicy, annualLeaveExpiryDay: rawPolicy.annualLeaveExpiryMonth == null ? null
    : Math.min(rawPolicy.annualLeaveExpiryDay ?? 1, expiryDaysInMonth(rawPolicy.annualLeaveExpiryMonth)) };

  const savePolicyMutation = useMutation({
    mutationFn: apiUpdateLeaveCompanyPolicy,
    onSuccess: () => {
      toast.success(t("leave.leavePolicySaved"));
      qc.invalidateQueries({ queryKey: ["leaveCompanyPolicy"] });
      setPolicyForm(null); // reset dirty state
    },
    onError: (err: Error & { code?: string }) => toast.error(err.code === "leave.errors.invalidExpiry" ? t(err.code) : err.message),
  });

  const isDirty = policyForm !== null;

  function setField<K extends keyof LeaveCompanyPolicy>(key: K, value: LeaveCompanyPolicy[K]) {
    setPolicyForm((prev) => ({
      ...(prev ?? effectivePolicy),
      [key]: value,
    }));
  }

  const { data: documentSettings, isLoading: documentSettingsLoading } = useQuery({
    queryKey: ["leaveDocumentSettings"],
    queryFn: apiGetLeaveDocumentSettings,
  });
  const [documentSettingsForm, setDocumentSettingsForm] =
    useState<LeaveDocumentSettings | null>(null);
  const effectiveDocumentSettings: LeaveDocumentSettings =
    documentSettingsForm ??
    documentSettings ?? {
      leaveType: "sick",
      enabled: true,
      daysWithoutDocument: 3,
      specialistRequiredAfterDays: 14,
      standardDocumentLabel: "Standard doctor medical document",
      standardDocumentDescription: "",
      specialistDocumentLabel: "Specialist doctor document",
      specialistDocumentDescription: "",
      mandatoryUploadsEnabled: true,
    };
  const documentSettingsDirty = documentSettingsForm !== null;
  const saveDocumentSettingsMutation = useMutation({
    mutationFn: apiUpdateLeaveDocumentSettings,
    onSuccess: () => {
      toast.success(t("leave.documentRulesSaved"));
      qc.invalidateQueries({ queryKey: ["leaveDocumentSettings"] });
      setDocumentSettingsForm(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  function setDocumentSetting<K extends keyof LeaveDocumentSettings>(
    key: K,
    value: LeaveDocumentSettings[K],
  ) {
    setDocumentSettingsForm((prev) => ({
      ...(prev ?? effectiveDocumentSettings),
      [key]: value,
    }));
  }

  // Recurring expiry supports leap day; materialization clamps it in non-leap years.
  const days = Array.from({ length: expiryDaysInMonth(effectivePolicy.annualLeaveExpiryMonth ?? 1) }, (_, i) => i + 1);

  return (
    <div className="space-y-6">

      {/* ── Leave Policy ─────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div className="p-5 space-y-5">

          {/* Annual */}
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Sun className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-sm font-semibold">{t("leave.annualLeave")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.daysPerYearLabel")}</Label>
                <Input
                  type="number"
                  min={0}
                  value={effectivePolicy.annualLeaveDays}
                  onChange={(e) => setField("annualLeaveDays", Number(e.target.value))}
                  disabled={policyLoading}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.maxCarryOverDays")}</Label>
                <Input
                  type="number"
                  min={0}
                  value={effectivePolicy.annualLeaveCarryOverMax}
                  onChange={(e) => setField("annualLeaveCarryOverMax", Number(e.target.value))}
                  disabled={policyLoading}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.carryOverExpiry")}</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={effectivePolicy.annualLeaveExpiryMonth?.toString() ?? "none"}
                    onValueChange={(v) => {
                      if (v === "none") {
                        setField("annualLeaveExpiryMonth", null);
                        setField("annualLeaveExpiryDay", null);
                      } else {
                        setField("annualLeaveExpiryMonth", Number(v));
                        setField("annualLeaveExpiryDay", Math.min(effectivePolicy.annualLeaveExpiryDay ?? 1, expiryDaysInMonth(Number(v))));
                      }
                    }}
                    disabled={policyLoading}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t("leave.noExpiry")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("leave.noExpiry")}</SelectItem>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {effectivePolicy.annualLeaveExpiryMonth != null && (
                    <Select
                      value={effectivePolicy.annualLeaveExpiryDay?.toString() ?? "1"}
                      onValueChange={(v) => setField("annualLeaveExpiryDay", Number(v))}
                      disabled={policyLoading}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {days.map((d) => (
                          <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Sick */}
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Stethoscope className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-sm font-semibold">{t("leave.sickLeave")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.daysPerYearLabel")}</Label>
                <Input
                  type="number"
                  min={0}
                  value={effectivePolicy.sickLeaveDays}
                  onChange={(e) => setField("sickLeaveDays", Number(e.target.value))}
                  disabled={policyLoading}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.daysWithoutDocument")}</Label>
                <Input
                  type="number"
                  min={0}
                  value={effectiveDocumentSettings.daysWithoutDocument}
                  onChange={(e) => setDocumentSetting("daysWithoutDocument", Number(e.target.value))}
                  disabled={documentSettingsLoading}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.specialistAfterDays")}</Label>
                <Input
                  type="number"
                  min={1}
                  value={effectiveDocumentSettings.specialistRequiredAfterDays}
                  onChange={(e) => setDocumentSetting("specialistRequiredAfterDays", Number(e.target.value))}
                  disabled={documentSettingsLoading}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("leave.mandatoryUploads")}</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={effectiveDocumentSettings.mandatoryUploadsEnabled}
                    onCheckedChange={(value) => setDocumentSetting("mandatoryUploadsEnabled", value)}
                    disabled={documentSettingsLoading}
                  />
                  <span className="text-xs text-muted-foreground">{t("leave.enabled", "Enabled")}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button
              onClick={() => {
                if (isDirty) savePolicyMutation.mutate(effectivePolicy);
                if (documentSettingsDirty) saveDocumentSettingsMutation.mutate(effectiveDocumentSettings);
              }}
              disabled={(!isDirty && !documentSettingsDirty) || savePolicyMutation.isPending || saveDocumentSettingsMutation.isPending}
              className="gap-1.5"
            >
              <Save className="h-3.5 w-3.5" />
              {(savePolicyMutation.isPending || saveDocumentSettingsMutation.isPending)
                ? t("leave.saving")
                : t("leave.saveLeavePolicy")}
            </Button>
          </div>
        </div>
      </div>

      <Separator />

      {/* ── Specialty Leave Types ────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-violet-500" />
            <div>
              <h3 className="font-semibold text-sm">{t("leave.specialtyLeaveTitle")}</h3>
              <p className="text-xs text-muted-foreground">
                {t("leave.specialtyLeaveDesc")}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditingType(null);
              setTypeDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t("leave.addType")}
          </Button>
        </div>

        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("leave.leaveTypeHead")}</TableHead>
                <TableHead className="text-center">{t("leave.daysAllowedHead")}</TableHead>
                <TableHead>{t("leave.paid")}</TableHead>
                <TableHead>{t("leave.documentHead")}</TableHead>
                <TableHead className="text-right">{t("leave.actionsHead")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specialtyTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                    {t("leave.noSpecialtyTypes")}
                  </TableCell>
                </TableRow>
              )}
              {specialtyTypes.map((lt) => (
                <TableRow key={lt.id}>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: lt.color }}
                      />
                      <span className="font-medium">{lt.label}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {lt.maxDaysPerYear != null ? (
                      <span className="font-medium">{lt.maxDaysPerYear}</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">{t("leave.noLimit")}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {lt.isPaid ? (
                      <Badge variant="secondary" className="text-xs">{t("leave.paid")}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">{t("leave.unpaid")}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {lt.requiresDocument === "over_3_working_days"
                      ? t("leave.overThreeDays")
                      : lt.requiresDocument === "never"
                      ? t("leave.neverRequired")
                      : t("leave.alwaysRequired")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingType(lt);
                          setTypeDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMutation.mutate(lt.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Type dialog ─────────────────────────────────────────────────── */}
      <TypeDialog
        open={typeDialogOpen}
        onOpenChange={setTypeDialogOpen}
        editing={editingType}
      />

      <Separator />

      {/* ── Extended Leave Framework ──────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BabyIcon className="h-4 w-4 text-pink-500" />
            <CardTitle className="text-base">{t("leave.extended.infoTitle")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <ExtendedLeavePolicyTab />
        </CardContent>
      </Card>
    </div>
  );
}
